import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import ApiEndpoints from "../../network/ApiEndPoints";
import ApiPaginateSearch from "../../component/ApiPaginateSearch";
import { CustomStyles } from "../../component/CustomStyle";
import { AddCustomer } from "./AddCustomer";
import CachedIcon from "@mui/icons-material/Cached";
import { dateToTime, dateToTime1, ddmmyy } from "../../utils/DateUtils";
import EditIcon from "@mui/icons-material/Edit";
import EditUser from "./EditUser";
import DeleteCustomer from "./DeleteCustomer";
import DeleteIcon from "@mui/icons-material/Delete";

let refresh;

export const CustomerUserdata = () => {
  const [apiData, setApiData] = useState([]);
  const [query, setQuery] = useState("");
  const [tabValue, setTabValue] = useState("All");
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const STATIC_ROLES = ["All", "Admin", "Developer","Backend", "Merchant", "ASM"];

  const uniqueRoles = useMemo(() => {
    if (!apiData?.length) return ["All"];
  
    const rolesFromApi = [...new Set(apiData.map((item) => item.role))];
  
    return ["All", ...rolesFromApi];
  }, [apiData]);
  

  const refreshFunc = () => {
    setQuery("");
    refresh?.();
  };

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
    setQuery(newValue === "All" ? "" : `role=${newValue}`);
  };
  useEffect(() => {
    refresh?.();
  }, [query]);

  const buttonStyles = (role) => ({
    minWidth: 110,
    padding: "8px 16px",
    borderRadius: "8px",
    backgroundColor: tabValue === role ? "#2F3B57" : "#f9f9f9",
    color: tabValue === role ? "#fff" : "#333",
    textTransform: "capitalize",
    fontSize: "15px",
    fontWeight: 600,
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      backgroundColor: tabValue === role ? "#1D2A44" : "#e0e0e0",
    },
  });

  const columns = [
    {
      name: "Created / Updated",
      selector: (row) => (
        <>
          <Tooltip title="Created At">
            <div>
              <span>{ddmmyy(row.created_at)}</span>
              <span style={{ marginLeft: "6px" }}>
                {dateToTime1(row.created_at)}
              </span>
            </div>
          </Tooltip>

          <Tooltip title="Updated At">
            <div style={{ marginBottom: "0.3em" }}>
              <span>{ddmmyy(row.updated_at)}</span>
              <span style={{ marginLeft: "6px" }}>
                {dateToTime1(row.updated_at)}
              </span>
            </div>
          </Tooltip>
        </>
      ),
    },

    {
      name: "Name",
      selector: (row) => (
        <div style={{ marginBottom: "12px", color: "#333", fontSize: "14px" }}>
          {row.name}
        </div>
      ),
      wrap: true,
      width: "150px",
    },
    {
      name: "Mobile",
      selector: (row) => (
        <div style={{ marginBottom: "12px", color: "#333", fontSize: "14px" }}>
          {row.mobile}
        </div>
      ),
      wrap: true,
      width: "150px",
    },

    {
      name: "Email",
      selector: (row) => (
        <div style={{ marginBottom: "12px", color: "#333", fontSize: "14px" }}>
          {row.email}
        </div>
      ),
      wrap: true,
      width: "120px",
    },
    {
      name: "Role",
      selector: (row) => (
        <div style={{ marginBottom: "12px", color: "#333", fontSize: "14px" }}>
          {row.role}
        </div>
      ),
      wrap: true,
      width: "120px",
    },
    {
      name: "SubRole",
      selector: (row) => (
        <div style={{ marginBottom: "12px", color: "#333", fontSize: "14px" }}>
          {row.sub_role}
        </div>
      ),
      wrap: true,
      width: "120px",
    },
    {
      name: "Total Tasks",
      selector: (row) => <div> {row.tasksCount || 0}</div>,
      width: "130px",
    },
    {
      name: "Completed Tasks",
      selector: (row) => <div> {row.completeCount || 0}</div>,
      width: "130px",
    },
    {
      name: "Pending Count",
      selector: (row) => <div>{row.tasksCount - row.completeCount || 0}</div>,
      width: "130px",
    },

    {
      name: "Actions",
      selector: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Edit Button */}
          <Tooltip title="Edit User">
            <IconButton
              onClick={() => {
                setSelectedUser(row);
                setOpenEdit(true);
              }}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>

          {/* Delete Button */}
          <Tooltip title="Delete User">
            <Box>
              <DeleteCustomer
                user={row}
                refresh={refreshFunc}
                iconColor="red"
              />
            </Box>
          </Tooltip>
        </Box>
      ),
      width: "150px",
    },
  ];
  return (
    <Box
      sx={{
        mt: 3,
        overflow: isSmallScreen ? "auto" : "visible",
        maxHeight: isSmallScreen ? "90vh" : "none",
      }}
    >
      <Grid
        container
        alignItems="center"
        justifyContent="flex-end"
        sx={{ position: "relative", mb: 0.7 }}
      >
        <Grid
          item
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            overflowX: isSmallScreen ? "auto" : "visible",
            whiteSpace: "nowrap",
            paddingBottom: isSmallScreen ? 1 : 0,
          }}
        >
          {STATIC_ROLES.map((role) => (
  <Button
    key={role}
    onClick={() => handleTabChange(role)}
    sx={buttonStyles(role)}
  >
    {role}
  </Button>
))}


          <AddCustomer refresh={refresh} />

          <Tooltip title="Refresh">
            <IconButton
              aria-label="refresh"
              sx={{ color: "#0F52BA" }}
              onClick={refreshFunc}
            >
              <CachedIcon className="refresh-purple" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>

      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <ApiPaginateSearch
          columns={columns}
          apiEnd={ApiEndpoints.GET_USERS_BIGG}
          card={false}
          pagename={"customer"}
          refresh={refreshFunc}
          apiData={apiData}
          setApiData={setApiData}
          tableStyle={CustomStyles}
          queryParam={query}
          returnRefetch={(ref) => (refresh = ref)}
        />
        {openEdit && selectedUser && (
          <EditUser
            open={openEdit}
            handleClose={() => setOpenEdit(false)}
            userData={selectedUser}
            refresh={refreshFunc}
          />
        )}
      </Grid>
    </Box>
  );
};
